import { Container } from 'aurelia-dependency-injection';
import { AureliaConfiguration } from 'aurelia-configuration';


const isLocalhostApi = () =>
    Container.instance.get(AureliaConfiguration).get('apiUrl') ===
    'https://127.0.0.1:8000/api/v2/';

const getFontBaseUrl = (website) =>
    isLocalhostApi()
        ? 'http://localhost:3000'
        : (website?.url ?? '').replace(/\/$/, '');

const toAbsoluteFontUrl = (url, base) =>
    /^https?:\/\//i.test(url)
        ? url
        : `${base}${url.startsWith('/') ? '' : '/'}${url}`;

const FONT_FORMAT_MAP = {
    woff2: 'woff2',
    woff: 'woff',
    ttf: 'truetype',
    otf: 'opentype',
    eot: 'embedded-opentype',
    svg: 'svg',
};


export const loadFonts = async ({ website }) => {
    const url = isLocalhostApi()
        ? 'http://localhost:3000/api/fonts'
        : `${website.url}/api/fonts`;

    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Basic c2lvOnNpbw==',
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    } catch {
        return '';
    }
};

const fontBlobUrlCache = new Map();

const fetchFontAsBlobUrl = async (fileUrl) => {
    if (fontBlobUrlCache.has(fileUrl)) {
        return fontBlobUrlCache.get(fileUrl);
    }
    const response = await fetch(fileUrl, {
        method: 'GET',
        headers: {
            Authorization: 'Basic c2lvOnNpbw==',
        },
    });

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    fontBlobUrlCache.set(fileUrl, blobUrl);
    return blobUrl;
};

export const buildFontFaceCss = async ({ fonts, website }) => {
    const base = getFontBaseUrl(website);

    const faces = await Promise.all(
        (fonts ?? []).flatMap((font) => {
            const familyName = font.family || font.name;

            return (font.weights ?? []).map(async (weight) => {
                const rawUrl = font.files?.[weight];
                if (!rawUrl) return '';

                const fileUrl = toAbsoluteFontUrl(rawUrl, base);
                const ext = fileUrl.split('?')[0].split('#')[0].split('.').pop().toLowerCase();
                const fontFormat = FONT_FORMAT_MAP[ext];

                let srcUrl = fileUrl;
                try {
                    srcUrl = await fetchFontAsBlobUrl(fileUrl);
                } catch (err) {
                    return '';
                }

                const src = fontFormat
                    ? `url('${srcUrl}') format('${fontFormat}')`
                    : `url('${srcUrl}')`;

                return `
                    @font-face {
                        font-family: '${familyName}';
                        src: ${src};
                        font-weight: ${weight};
                        font-style: normal;
                        font-display: swap;
                    }`;
            });
        })
    );
    return faces.join('\n');
};

const getFontVariableName = (font) =>
    font.variable || `--font-${font.name.toLowerCase().replace(/\s+/g, '-')}`;

export const buildFontVariablesCss = ({ fonts }) => {
    const vars = (fonts ?? [])
        .map((f) => `    ${getFontVariableName(f)}: '${f.family}';`)
        .join('\n');

    return `html {\n${vars}\n}`;
};

export const buildFontWeightCss = ({ fonts }) =>
    (fonts ?? [])
        .flatMap((font) => {
            const className = font.name.toLowerCase().replace(/\s+/g, '-');

            return (font.weights ?? []).map(
                (w) => `
.${className}-w${w} {
    font-family: '${font.family}', serif;
    font-weight: ${w};
}`
            );
        })
        .join('\n');


export const registerFontsInStyleManager = ({ editor, fonts }) => {
    const fontOptions = (fonts ?? []).map((font) => {
        const value = `var(${getFontVariableName(font)})`;
        return {
            id: value,
            value,
            name: font.name,
            label: font.name,
        };
    });

    const fontProp = editor.StyleManager.getProperty('typography', 'font-family');

    if (fontProp) {
        const existing = fontProp.get('options') ?? fontProp.get('list') ?? [];
        const existingIds = new Set(existing.map((o) => o.id ?? o.value));
        const merged = [
            ...existing,
            ...fontOptions.filter((o) => o.id && !existingIds.has(o.id)),
        ];
        fontProp.set('options', merged);
        fontProp.set('list', merged);
    } else {
        editor.StyleManager.addProperty('typography', {
            property: 'font-family',
            name: 'Font',
            options: fontOptions,
        });
    }
};